/** @param {unknown} v */
function stripId(v) {
  if (v == null) return "";
  const s = String(v).trim();
  return s;
}

/** Stored rows from GET /app/notifications include `deeplinkPatient` (healbid://…?matchRequestId=…). */
function idsFromPatientDeeplink(n) {
  const raw = stripId(n?.deeplinkPatient);
  if (!raw || raw.indexOf("?") < 0) return { matchRequestId: "", bidLogId: "" };
  try {
    const sp = new URLSearchParams(raw.slice(raw.indexOf("?") + 1));
    return {
      matchRequestId: stripId(sp.get("matchRequestId")),
      bidLogId: stripId(sp.get("bidLogId")),
    };
  } catch {
    return { matchRequestId: "", bidLogId: "" };
  }
}

/**
 * Best-effort deep link for a patient notification or socket payload.
 * @param {Record<string, unknown>} payload
 * @returns {string | null}
 */
export function getPatientNotificationPath(payload) {
  const n = payload && typeof payload === "object" ? payload : {};
  const t = String(n.type || "").toLowerCase();
  const prescriptionId = stripId(n.prescriptionId);
  if (t === "prescription_rejected" && prescriptionId) {
    return `/patient-documents?reupload=${encodeURIComponent(prescriptionId)}`;
  }
  if (t === "prescription_bulk_rejected") return "/patient-documents";
  if (
    t === "prescription_upload_patient" ||
    t === "prescription_accepted" ||
    t === "prescription_bulk_accepted"
  ) {
    return prescriptionId
      ? `/patient-documents?reupload=${encodeURIComponent(prescriptionId)}`
      : "/patient-documents";
  }
  if (t === "patient_account_approved" || t === "patient_account_rejected") {
    return "/HealBidDashboard";
  }
  const conversationId = stripId(n.conversationId);
  if (conversationId) {
    return `/patient-chats?openConversation=${encodeURIComponent(conversationId)}`;
  }
  const appointmentId = stripId(n.appointmentId);
  if (appointmentId) return `/appointment`;
  const fromDeeplink = idsFromPatientDeeplink(n);
  const matchRequestId = stripId(n.matchRequestId) || fromDeeplink.matchRequestId;
  if (matchRequestId) return `/patient-match-request/${encodeURIComponent(matchRequestId)}`;
  const bidLogId = stripId(n.bidLogId ?? n.bidId) || fromDeeplink.bidLogId;
  if (bidLogId) return `/patient-requests`;
  return null;
}

export function getPatientNotificationPathOrDefault(payload) {
  return getPatientNotificationPath(payload) || "/HealBidDashboard";
}

/**
 * Deep link for doctor/hospital (chats, appointments, or notifications list).
 * @param {Record<string, unknown>} payload
 * @param {{ isHospital?: boolean }} opts
 * @returns {string | null}
 */
export function getProviderNotificationPath(payload, opts = {}) {
  const n = payload && typeof payload === "object" ? payload : {};
  const isHospital = Boolean(opts.isHospital);
  const chats = isHospital ? "/hospital-chats" : "/doctor-chats";
  const appointments = isHospital ? "/hospital-appointments" : "/doctor-appointments";
  const notificationsList = isHospital ? "/hospital-notifications" : "/doctor-notifications";
  const settings = isHospital ? "/hospital-settings" : "/doctor-settings";

  const bidLogId = stripId(n.bidLogId ?? n.bidId);
  const conversationId = stripId(n.conversationId);
  const appointmentId = stripId(n.appointmentId);
  const matchRequestId = stripId(n.matchRequestId);
  const t = String(n.type || "").toLowerCase();

  if (bidLogId) return `${chats}?openBid=${encodeURIComponent(bidLogId)}`;
  if (conversationId) return `${chats}?openConversation=${encodeURIComponent(conversationId)}`;
  if (appointmentId) return appointments;
  if (matchRequestId) {
    return `${chats}?openMatchRequest=${encodeURIComponent(matchRequestId)}`;
  }
  if (t === "admin_rating") return settings;
  if (t.includes("appointment") || t.includes("booking") || t.includes("visit")) return appointments;
  if (stripId(n.providerNotificationId)) return notificationsList;
  return null;
}

/**
 * @param {Record<string, unknown>} payload
 * @param {{ isHospital?: boolean, dashboardPath?: string }} opts
 */
export function getProviderNotificationPathOrDefault(payload, opts = {}) {
  const dash = opts.dashboardPath || (opts.isHospital ? "/hospital-dashboard" : "/doctor-dashboard");
  return getProviderNotificationPath(payload, opts) || dash;
}
